import { useCallback, useState, type PointerEvent } from 'react';

export const EXCLUSIVE_SELECT_TRIGGER_CLASS = 'pointer-events-auto';

function findExclusiveSelectTriggerInGroup(
  clientX: number,
  clientY: number,
  groupId: string,
): HTMLElement | null {
  const container = document.querySelector(`[data-exclusive-select-group="${groupId}"]`);
  if (!container) return null;

  for (const trigger of container.querySelectorAll<HTMLElement>('[data-exclusive-select-id]')) {
    const rect = trigger.getBoundingClientRect();
    if (
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom
    ) {
      return trigger;
    }
  }

  return null;
}

function findExclusiveSelectTriggerFromOutsideEvent(event: Event, groupId: string): HTMLElement | null {
  const detail = (event as CustomEvent<{ originalEvent: PointerEvent }>).detail;
  const originalEvent = detail?.originalEvent;

  if (originalEvent && Number.isFinite(originalEvent.clientX) && Number.isFinite(originalEvent.clientY)) {
    const fromPoint = findExclusiveSelectTriggerInGroup(
      originalEvent.clientX,
      originalEvent.clientY,
      groupId,
    );
    if (fromPoint) return fromPoint;
  }

  const fromTarget = originalEvent?.target ?? event.target;
  if (!(fromTarget instanceof Element)) return null;

  const trigger = fromTarget.closest('[data-exclusive-select-id]');
  if (!(trigger instanceof HTMLElement) || !trigger.dataset.exclusiveSelectId) return null;
  return trigger.closest(`[data-exclusive-select-group="${groupId}"]`) ? trigger : null;
}

export function useExclusiveSelect<TOpenId extends string>(groupId: string) {
  const [openId, setOpenId] = useState<TOpenId | null>(null);

  const onOpenChange = useCallback(
    (id: TOpenId) => (open: boolean) => {
      setOpenId((current) => {
        if (open) return id;
        if (current === id) return null;
        return current;
      });
    },
    [],
  );

  const onSelectContentOutside = useCallback(
    (event: Event) => {
      const trigger = findExclusiveSelectTriggerFromOutsideEvent(event, groupId);
      if (!trigger) return;

      const nextId = trigger.dataset.exclusiveSelectId as TOpenId | undefined;
      if (!nextId) return;

      event.preventDefault();
      setOpenId(nextId);
    },
    [groupId],
  );

  const onTriggerPointerDown = useCallback(
    (id: TOpenId) => (event: PointerEvent<HTMLButtonElement>) => {
      setOpenId((current) => {
        if (current !== null && current !== id) {
          event.preventDefault();
          return id;
        }
        return current;
      });
    },
    [],
  );

  return {
    openId,
    setOpenId,
    onOpenChange,
    onSelectContentOutside,
    onTriggerPointerDown,
    exclusiveSelectGroupId: groupId,
    exclusiveSelectTriggerClass: EXCLUSIVE_SELECT_TRIGGER_CLASS,
  };
}
